import React, { useState } from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import { css, Theme } from '@emotion/react';
import { mixins, colors } from '../../core/styles/theme/default';
import { Grid, GridItem } from '../../helpers';
import { Link, ModuleHeader } from '../../primitives';
import Spacer from '../../helpers/Spacer';
import Slider from '../Slider';

const StyledGenericTeaserModule = styled.div(
  ({
    background,
    textColor,
  }: {
    background?: string;
    textColor?: 'light';
  }) => css`
    ${background &&
    css`
      position: relative;
      ${mixins.responsiveStyles({
        'padding-top': {
          zero: rem(40),
          medium: rem(64),
        },
        'padding-bottom': {
          zero: 0,
          medium: rem(24),
        },
      })}

      &::before {
        content: '';
        width: 100vw;
        height: 100%;
        position: absolute;
        top: 0;
        background-color: ${background};

        ${mixins.responsiveStyles({
          left: {
            zero: rem(-16),
            medium: rem(-24),
            large: rem(-48),
            wide: `calc(-1 * ((100vw - ${rem(1088)} ) / 2))`,
            ultra: `calc(-1 * ((100vw - ${rem(1260)} ) / 2))`,
          },
        })}
      }

      ${textColor === 'light' &&
      css`
        div.swiper.swiper-initialized .swiper-pagination {
          &-bullet {
            border-color: ${colors.white};

            &-active {
              background: ${colors.white};
            }
          }

          &-lock + .swiper-wrapper {
            ${mixins.responsiveStyles({
              'margin-bottom': {
                zero: rem(40),
                medium: rem(64),
              },
            })}
          }
        }
      `}
    `};
  `,
);

const StyledTeaserCard = styled.div`
  height: 100%;

  &[data-type] {
    & > .spacer {
      display: none;
    }

    & > * {
      margin: 0;
      width: 100%;
      max-width: 100%;
      height: 100%;
      box-sizing: border-box;
    }
  }

  &[data-type='ContentfulPublication'] > div {
    padding: 0;
  }

  &[data-type='ContentfulStoryHub'] > article {
    padding: 0;

    & > div {
      padding: 0;
    }
  }

  &[data-style='slider'] {
    min-height: ${rem(340)};

    & > * {
      min-height: inherit;
    }

    &[data-type='ContentfulEventHub'],
    &[data-type='ContentfulPublication'] {
      min-height: 0;
    }
  }
`;

const StyledMoreLink = styled(Link)(
  ({ textColor, theme }: { textColor?: 'light'; theme?: Theme }) => css`
    position: relative;
    z-index: 1;

    .m-richtext & {
      text-decoration: none;
    }

    ${textColor === 'light' &&
    css`
      color: ${colors.white};

      &:hover {
        color: ${colors.white};
      }

      svg path {
        fill: ${colors.white};
        stroke: ${colors.white};
      }
    `}

    ${theme?.stylesExtension?.genericTeaserModule?.moreLink}
  `,
);

const StyledGrid = styled.div(
  ({ displayStyle }: { displayStyle?: string }) => css`
    display: grid;

    ${mixins.responsiveStyles({
      'grid-template-columns': {
        zero: '1fr',
        medium: 'repeat(2, 1fr)',
      },
      'grid-gap': {
        zero: `${rem(16)}`,
        medium: `${rem(24)}`,
        ultra: `${rem(32)}`,
      },
    })}

    ${displayStyle === 'Events' &&
    css`
      ${mixins.responsiveStyles({
        'grid-template-columns': {
          zero: '1fr',
          medium: '1fr',
        },
        'grid-gap': {
          zero: `${rem(10)}`,
          medium: `${rem(10)}`,
          ultra: `${rem(10)}`,
        },
      })}
    `}
  `,
);

const GenericTeaserModule = ({
  title,
  lead,
  items,
  displayStyle,
  displayAsGrid,
  textColor,
  background,
  showMoreLink,
}: GenericTeaserModuleProps) => {
  const [isPaginationVisible, setIsPaginationVisible] = useState(true);
  const hasBackground = !!background;
  const slidesPerView = displayStyle === 'Events' ? 1 : 2;

  return (
    <StyledGenericTeaserModule
      className="m-genericteasermodule"
      background={background}
      textColor={textColor}
    >
      <Grid columns={12}>
        <GridItem size={{ zero: 12, ultra: 8 }} push={{ zero: 0, ultra: 2 }}>
          {title && (
            <>
              <ModuleHeader title={title} lead={lead} color={textColor} />
              <Spacer space="gamma" />
            </>
          )}

          {items?.length ? (
            displayAsGrid ? (
              <>
                <StyledGrid displayStyle={displayStyle}>
                  {items.map((item: GenericTeaserModuleItemProps, i) => (
                    <StyledTeaserCard
                      key={`${item.id}-${i}`}
                      data-type={item.type}
                      data-style={'grid'}
                    >
                      {item.content}
                    </StyledTeaserCard>
                  ))}
                </StyledGrid>
                {showMoreLink && <Spacer space="epsilon" />}
              </>
            ) : (
              <>
                <Slider
                  showHiddenSlides
                  disableOpacity={hasBackground}
                  slidesPerView={slidesPerView}
                  onPaginationVisibilityChange={(isVisible) =>
                    setIsPaginationVisible(isVisible)
                  }
                >
                  {items.map((item: GenericTeaserModuleItemProps, i) => (
                    <StyledTeaserCard
                      key={`${item.id}-${i}`}
                      data-type={item.type}
                      data-style={'slider'}
                    >
                      {item.content}
                    </StyledTeaserCard>
                  ))}
                </Slider>
                {showMoreLink && !isPaginationVisible && (
                  <Spacer space="epsilon" />
                )}
              </>
            )
          ) : null}

          {showMoreLink ? (
            <>
              <StyledMoreLink
                linkText={showMoreLink.text}
                link={{
                  href: showMoreLink.href,
                  title: showMoreLink.title,
                }}
                iconAfter="AnimatedIconArrowRight"
                className="animate"
                textColor={textColor}
              />
              {hasBackground && <Spacer space="zeta" />}
            </>
          ) : null}
        </GridItem>
      </Grid>
    </StyledGenericTeaserModule>
  );
};

export type GenericTeaserModuleItemProps = {
  type: string;
  id: string;
  content: React.ReactNode;
};

export type GenericTeaserModuleProps = {
  title?: string;
  lead?: string;
  items: GenericTeaserModuleItemProps[];
  displayStyle?: string;
  displayAsGrid?: boolean;
  textColor?: 'light';
  background?: string;
  showMoreLink?: {
    text: string;
    title: string;
    href: string;
    target?: string;
  };
};

export default GenericTeaserModule;
